import { formatCalendarTime } from 'aos-helpers/src/helpers/TimeFormat'
import { severityToneMap } from 'aos-services/src/services/common/types/AosSeverity'
import { assigneesLabel } from 'aos-services/src/services/tasks/types/task/BaseTask'
import { TaskSnapshot } from 'aos-services/src/services/tasks/types/TaskSnapshot'
import { translateTaskTitle } from 'aos-services/src/services/tasks/types/TaskStatus'
import {
    Box,
    HStack,
    Icon,
    Pressable,
    RecurranceIcon,
    severityToneStatus,
    StatusStripe,
    Text,
    VStack,
} from 'aos-ui-universal'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

import { taskCategoryIcon } from '../../utils/taskIconMappers'
import { TaskMetaIcons } from './TaskMetaIcons'

interface TasksListItemProps {
    task: TaskSnapshot
    isSelected?: boolean
    onOpen(): void
}

export const TasksListItem: FC<TasksListItemProps> = ({ task, onOpen, isSelected }) => {
    const { t: translate } = useTranslation()

    return (
        <Pressable onPress={onOpen}>
            <HStack
                className={twMerge(
                    'relative mb-5 h-[76px] items-center overflow-hidden rounded bg-aos-bg-surface pl-5 pr-4',
                    'hover:bg-aos-bg-elevation-hover active:bg-aos-bg-elevation-pressed',
                    isSelected && 'bg-aos-bg-elevation',
                )}
                space='lg'
            >
                <StatusStripe
                    status={severityToneStatus[severityToneMap[task.severity]]}
                    className='absolute inset-y-0 left-0 w-1 rounded-none'
                />
                <Icon
                    as={taskCategoryIcon[task.category]}
                    size={24}
                    className='shrink-0 text-aos-text-default'
                />
                <Text
                    size='body'
                    weight='medium'
                    tone='default'
                    numberOfLines={1}
                    className='ml-3 truncate flex-[2]'
                >
                    {task.title}
                </Text>

                {task.hasRecurrence ? (
                    <Icon as={RecurranceIcon} size={24} className='text-aos-text-default' />
                ) : null}
                <Box
                    className={twMerge(
                        // `min-w-fit`: the pill fills its column so every row's badge is
                        // identical on the list page, but in the narrow dashboard tile
                        // (TaskCardList) it falls back to its label rather than cutting it.
                        'min-w-fit flex-1 rounded border border-solid border-graphics-grey px-2 py-1',
                        task.overdue && 'border-graphics-yellow',
                    )}
                >
                    <Text
                        size='caption'
                        weight='medium'
                        tone='default'
                        numberOfLines={1}
                        className='truncate text-center uppercase'
                    >
                        {translateTaskTitle(translate)(task.status)}
                    </Text>
                </Box>
                <VStack className='justify-center flex-1'>
                    <Text
                        className='text-center'
                        size='body'
                        tone={task.overdue ? 'warning' : 'default'}
                    >
                        {formatCalendarTime(translate)(task.startTime)}
                    </Text>
                    {task.assignees.length > 0 && (
                        <Text
                            numberOfLines={1}
                            className='w-full truncate text-center'
                            size='caption'
                            tone='muted'
                        >
                            {assigneesLabel(task.assignees)}
                        </Text>
                    )}
                </VStack>
                <TaskMetaIcons
                    hasLocation={task.hasLocation}
                    attachmentsLength={task.attachmentsLength}
                    hasChecklist={task.hasChecklist}
                />
            </HStack>
        </Pressable>
    )
}
